/**
 * 저장되지 않은 값(거절) 안내.
 *
 * 저장 검증은 어느 셀이 왜 막혔는지 이미 안다. 요약 한 줄로 버리지 않고 목록으로
 * 들고 있다가 해당 셀로 이동시킨다. 고친 셀은 목록에서 자동으로 빠지고, 전부
 * 빠지면 안내가 닫힌다.
 *
 * WXBRL-2647 로 비교 화면이 값 편집기 안으로 들어오기 전에는
 * `app/ixd-workbook-match/view.tsx` 가 이 로직을 들고 있었다.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { FsType, IxdChunk } from "@/apis/ixd-editor/types";
import type { IxdCellNotice } from "@/components/ixd-preview";
import {
  buildRejectionGuideItems,
  findTopmostChangeLocation,
  type SaveRejectionPopoverProps,
} from "@/components/ixd-workbook-match";
import type { useIxdEditSession } from "@/hooks/ixd-edit-session";
import type { CellIdentityLocation } from "@/utils/ixd-cell-model";
import { logIxdEditorAction } from "@/utils/usage-log";

type EditSession = ReturnType<typeof useIxdEditSession>;

/**
 * 가상화된 비교 행은 화면에 들어오기 전까지 추정 높이(500px)로 자리만 잡는다.
 * 실제 높이로 그려지는 순간 아래 좌표가 전부 밀리므로, 한 번 이동한 뒤 레이아웃이
 * 가라앉으면 좌표를 다시 읽어 맞춘다.
 */
const LAZY_RENDER_CORRECTION_DELAY_MS = 180;

/** 포커스 대상 셀 DOM. renderer가 심는 위치 uid로 찾는다. */
const findLocationCell = (
  container: HTMLElement | null,
  location: CellIdentityLocation,
): HTMLElement | null => {
  if (!container || !location.uid) return null;
  const escaped = CSS.escape(location.uid);
  const cell = container.querySelector(
    `[data-cell-identity-location-uid="${escaped}"], #${escaped}`,
  );
  return cell instanceof HTMLElement ? cell : null;
};

interface UseRejectionGuideInput {
  session: EditSession;
  /** 화면이 보여주는 유형 — 다른 쪽 거절은 이 화면에서 이동시킬 수 없다 */
  fsType: FsType;
  /** 본문이 그리는 chunk 목록 — 안내 순서가 곧 화면 배치 순서다 */
  chunks: IxdChunk[];
  /** 본문 스크롤 컨테이너 — 이동 후 좌표 보정에 쓴다 */
  listContainerRef: React.RefObject<HTMLDivElement>;
  onRequestCellFocus: (location: CellIdentityLocation) => void;
}

export const useRejectionGuide = ({
  session,
  fsType,
  chunks,
  listContainerRef,
  onRequestCellFocus,
}: UseRejectionGuideInput) => {
  const { identityLocationsByKey, cellIdentityByNormalizedCellId } = session;
  const findRejectionLocation = useCallback(
    (changeKey: string) =>
      findTopmostChangeLocation([{ fsType, changeKey }], {
        chunks,
        identityLocationsByKey,
        identityKeyByNormalizedCellId: cellIdentityByNormalizedCellId,
      }),
    [cellIdentityByNormalizedCellId, chunks, fsType, identityLocationsByKey],
  );
  const rejectionItems = useMemo(
    () =>
      buildRejectionGuideItems({
        rejections: session.rejections,
        fsType,
        cellChanges: session.cellChanges,
        findLocation: findRejectionLocation,
        chunks,
      }),
    [
      chunks,
      findRejectionLocation,
      fsType,
      session.cellChanges,
      session.rejections,
    ],
  );

  // 저장 1회 = rejections 배열 하나. 저장할 때마다 새 배열이고 거절 0건이면 고정
  // 참조(NO_REJECTIONS)라, 배열 참조 자체가 "저장 사건"의 식별자다. 이 리셋을
  // effect로 하면 한 프레임 동안 이전 인덱스가 유효해 포커스가 두 번 나간다 —
  // 파생 상태는 렌더 중에 조정한다(principle-maintainability-first).
  const [guideCursor, setGuideCursor] = useState({
    rejections: session.rejections,
    index: 0,
    saveId: 0,
  });
  if (guideCursor.rejections !== session.rejections) {
    setGuideCursor({
      rejections: session.rejections,
      index: 0,
      saveId: guideCursor.saveId + 1,
    });
  }
  // 앞쪽 항목을 고쳐 목록이 줄면 표시 인덱스만 당긴다. 커서는 건드리지 않는다 —
  // 커서가 바뀌면 포커스가 따라 움직이는데, 셀을 고친 것은 이동 요청이 아니다.
  const currentRejectionIndex = Math.min(
    guideCursor.index,
    Math.max(rejectionItems.length - 1, 0),
  );
  const currentRejection = rejectionItems[currentRejectionIndex];

  // 저장 한 번에 한 번 센다 — saveId 는 저장(rejections 교체)마다 오른다. 안내 안의
  // 클릭만으로는 "저장했는데 일부가 막혔다"는 사건 자체가 보이지 않는다.
  // 같은 저장 안에서도 연결·별도 전환이나 되돌리기로 목록이 비었다 다시 차므로,
  // 이미 센 saveId 를 기억해 두 번 세지 않는다.
  const hasRejectionGuide = rejectionItems.length > 0;
  const loggedRejectionSaveIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (!hasRejectionGuide) return;
    if (loggedRejectionSaveIdRef.current === guideCursor.saveId) return;
    loggedRejectionSaveIdRef.current = guideCursor.saveId;
    logIxdEditorAction("저장 거절 안내 열림");
  }, [guideCursor.saveId, hasRejectionGuide]);

  // 포커스 보정 타이머. 사용자가 직접 스크롤하면 취소한다(scroll spy 가 호출).
  const focusCorrectionTimerRef = useRef<number | null>(null);
  const cancelFocusCorrection = useCallback(() => {
    if (focusCorrectionTimerRef.current === null) return;
    window.clearTimeout(focusCorrectionTimerRef.current);
    focusCorrectionTimerRef.current = null;
  }, []);

  // 포커스를 옮기는 사건은 둘뿐이다: 저장 직후(saveId) · 이전/다음 클릭(index).
  // 항목의 changeKey를 키에 넣으면 셀을 고쳐 목록이 당겨질 때도 화면이 튄다.
  const currentRejectionRef = useRef(currentRejection);
  currentRejectionRef.current = currentRejection;
  const rejectionFocusKey = currentRejection
    ? `${guideCursor.saveId}:${guideCursor.index}`
    : null;
  useEffect(() => {
    const location = currentRejectionRef.current?.location;
    if (!location) return;
    onRequestCellFocus(location);
    // 이동 직후에는 목적지 주변 행이 아직 추정 높이라 셀이 중앙에서 밀려 있고,
    // 표 폭도 그때 확정돼 가로 위치까지 어긋난다. 레이아웃이 가라앉은 뒤 한 번만
    // 다시 맞춘다 — 세로·가로를 같이 본다(여기만 적용, 공용 포커스는 그대로).
    // 그 사이 사용자가 직접 스크롤하면 cancelFocusCorrection 이 이 타이머를 끈다.
    focusCorrectionTimerRef.current = window.setTimeout(() => {
      focusCorrectionTimerRef.current = null;
      findLocationCell(listContainerRef.current, location)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
    }, LAZY_RENDER_CORRECTION_DELAY_MS);
    return cancelFocusCorrection;
  }, [
    cancelFocusCorrection,
    listContainerRef,
    onRequestCellFocus,
    rejectionFocusKey,
  ]);

  // 거절된 셀은 표에서도 빨간 표식으로 찾을 수 있어야 한다 — 패널만 보고
  // 스크롤을 되짚게 하지 않는다.
  //
  // 이 Map은 preview config context로 들어가 표의 모든 셀이 구독한다. 편집할 때마다
  // 새 참조를 만들면 셀 하나를 고쳐도 그려진 표 전체가 다시 렌더된다. 내용(위치·사유)이
  // 같으면 참조를 유지하려고 문자열 키로 메모한다.
  const rejectedNoticeKey = rejectionItems
    .map((item) => `${item.location?.identityKey ?? ""}\u0000${item.reason}`)
    .join("\u0001");
  const cellNotices = useMemo(() => {
    const notices = new Map<string, IxdCellNotice>();
    for (const entry of rejectedNoticeKey.split("\u0001")) {
      const [identityKey, reason] = entry.split("\u0000");
      if (!identityKey) continue;
      notices.set(identityKey, { tone: "blocked", description: reason });
    }
    return notices.size > 0 ? notices : undefined;
  }, [rejectedNoticeKey]);

  const { clearRejections } = session;
  const guideLogIds = {
    rejectedCount: rejectionItems.length,
    position: currentRejectionIndex + 1,
  };
  const guide: SaveRejectionPopoverProps | null = currentRejection
    ? {
        count: rejectionItems.length,
        position: currentRejectionIndex + 1,
        where: currentRejection.where,
        value: currentRejection.value,
        reason: currentRejection.reason,
        onPrev: () => {
          logIxdEditorAction("저장 거절 안내 이전", guideLogIds);
          setGuideCursor((cursor) => ({
            ...cursor,
            index: Math.max(currentRejectionIndex - 1, 0),
          }));
        },
        onNext: () => {
          logIxdEditorAction("저장 거절 안내 다음", guideLogIds);
          setGuideCursor((cursor) => ({
            ...cursor,
            index: Math.min(
              currentRejectionIndex + 1,
              rejectionItems.length - 1,
            ),
          }));
        },
        onClose: () => {
          logIxdEditorAction("저장 거절 안내 닫기", guideLogIds);
          clearRejections();
        },
      }
    : null;

  return { guide, cellNotices, cancelFocusCorrection };
};
